import
{
    IonButton,
    IonCard,
    IonCardContent,
    IonContent,
    IonLabel,
    IonPage,
    IonSpinner,
    useIonRouter
} from '@ionic/react';

import { useL10n } from '@ews/react-localization-context';

import { User } from '@ews/react-data';
import React, { ReactNode, useCallback, useState } from 'react';
import Checkbox from '../Checkbox/Wrapper';
import { useMyProfile } from '../Login/LoginProvider';
import { useNamedRoutes } from '../NamedRoutes';
import { useModifyUser } from '../ReactData/user';
import styles from './TermsAndConditionsModal.module.scss';
import { TermsAndConditionsValues } from './types';
import { isUserImpersonated } from '../Authorization';


export const TermsAndConditionsContent: React.FC<{}> = () =>
{
    const { translate: t } = useL10n();

    return <>

        <h1>{t('TERMS_AND_CONDITIONS_TITLE')}</h1>
        <p>{t('TERMS_AND_CONDITIONS_SUBTEXT')}</p>

        <h2>{t('TERMS_AND_CONDITIONS_SERVICE_DESCRIPTION_HEADLINE')}</h2>
        <p>{t('TERMS_AND_CONDITIONS_SERVICE_DESCRIPTION_PARAGRAPH')}</p>

        <h2>{t('TERMS_AND_CONDITIONS_USE_OF_SERVICE_HEADLINE')}</h2>
        <p>{t('TERMS_AND_CONDITIONS_USE_OF_SERVICE_PARAGRAPH')}</p>

        <h2>{t('TERMS_AND_CONDITIONS_SERVICE_COMPONENTS_HEADLINE')}</h2>
        <p>{t('TERMS_AND_CONDITIONS_SERVICE_COMPONENTS_PARAGRAPH')}</p>

        <h2>{t('TERMS_AND_CONDITIONS_ELECTRONIC_COMMUNICATION_HEADLINE')}</h2>
        <p>{t('TERMS_AND_CONDITIONS_ELECTRONIC_COMMUNICATION_PARAGRAPH')}</p>

        <h2>{t('TERMS_AND_CONDITIONS_MODIFICATIONS_HEADLINE')}</h2>
        <p>{t('TERMS_AND_CONDITIONS_MODIFICATIONS_PARAGRAPH')}</p>

        <h2>{t('TERMS_AND_CONDITIONS_TERMINATION_HEADLINE')}</h2>
        <p>{t('TERMS_AND_CONDITIONS_TERMINATION_PARAGRAPH_1')}</p>

        <ul>
            <li>{t('TERMS_AND_CONDITIONS_TERMINATION_PARAGRAPH_BULLETPOINT_1')}</li>
            <li>{t('TERMS_AND_CONDITIONS_TERMINATION_PARAGRAPH_BULLETPOINT_2')}</li>
            <li>{t('TERMS_AND_CONDITIONS_TERMINATION_PARAGRAPH_BULLETPOINT_3')}</li>
            <li>{t('TERMS_AND_CONDITIONS_TERMINATION_PARAGRAPH_BULLETPOINT_4')}</li>
            <li>{t('TERMS_AND_CONDITIONS_TERMINATION_PARAGRAPH_BULLETPOINT_5')}</li>
            <li>{t('TERMS_AND_CONDITIONS_TERMINATION_PARAGRAPH_BULLETPOINT_6')}</li>
            <li>{t('TERMS_AND_CONDITIONS_TERMINATION_PARAGRAPH_BULLETPOINT_7')}</li>
        </ul>

        <p>{t('TERMS_AND_CONDITIONS_TERMINATION_PARAGRAPH_2')}</p>

        <h2>{t('TERMS_AND_CONDITIONS_DATA_PROTECTION_HEADLINE')}</h2>

        <h3>{t('TERMS_AND_CONDITIONS_DATA_PROTECTION_SUBHEADLINE_PERSONAL_DATA')}</h3>
        <p>{t('TERMS_AND_CONDITIONS_DATA_PROTECTION_PARAGRAPH_PERSONAL_DATA')}</p>

        <h3>{t('TERMS_AND_CONDITIONS_DATA_PROTECTION_SUBHEADLINE_COOKIES')}</h3>
        <p>{t('TERMS_AND_CONDITIONS_DATA_PROTECTION_PARAGRAPH_COOKIES')}</p>

        <h3>{t('TERMS_AND_CONDITIONS_DATA_PROTECTION_SUBHEADLINE_GOOGLE_MAPS')}</h3>
        <p>{t('TERMS_AND_CONDITIONS_DATA_PROTECTION_PARAGRAPH_GOOGLE_MAPS')}</p>

        <h2>{t('TERMS_AND_CONDITIONS_LIMITATION_OF_LIABILITY_HEADLINE')}</h2>
        <p>{t('TERMS_AND_CONDITIONS_LIMITATION_OF_LIABILITY_PARAGRAPH_1')}</p>

        <ul>
            <li>{t('TERMS_AND_CONDITIONS_LIMITATION_OF_LIABILITY_PARAGRAPH_BULLETPOINT_1')}</li>
            <li>{t('TERMS_AND_CONDITIONS_LIMITATION_OF_LIABILITY_PARAGRAPH_BULLETPOINT_2')}</li>
            <li>{t('TERMS_AND_CONDITIONS_LIMITATION_OF_LIABILITY_PARAGRAPH_BULLETPOINT_3')}</li>
            <li>{t('TERMS_AND_CONDITIONS_LIMITATION_OF_LIABILITY_PARAGRAPH_BULLETPOINT_4')}</li>
            <li>{t('TERMS_AND_CONDITIONS_LIMITATION_OF_LIABILITY_PARAGRAPH_BULLETPOINT_5')}</li>
            <li>{t('TERMS_AND_CONDITIONS_LIMITATION_OF_LIABILITY_PARAGRAPH_BULLETPOINT_6')}</li>
            <li>{t('TERMS_AND_CONDITIONS_LIMITATION_OF_LIABILITY_PARAGRAPH_BULLETPOINT_7')}</li>
            <li>{t('TERMS_AND_CONDITIONS_LIMITATION_OF_LIABILITY_PARAGRAPH_BULLETPOINT_8')}</li>
            <li>{t('TERMS_AND_CONDITIONS_LIMITATION_OF_LIABILITY_PARAGRAPH_BULLETPOINT_9')}</li>
        </ul>

        <h2>{t('TERMS_AND_CONDITIONS_COPYRIGHT_HEADLINE')}</h2>
        <p>{t('TERMS_AND_CONDITIONS_COPYRIGHT_PARAGRAPH')}</p>

        <h2>{t('TERMS_AND_CONDITIONS_GENERAL_TERMS_HEADLINE')}</h2>
        <p>{t('TERMS_AND_CONDITIONS_GENERAL_TERMS_PARAGRAPH')}</p>

        {/* 🔽 NEW SUPPORT GUIDELINE SECTION */}
        <h1>{t('TERMS_AND_CONDITIONS_SUPPORT_GUIDELINE_HEADLINE')}</h1>
        <p>{t('TERMS_AND_CONDITIONS_SUPPORT_GUIDELINE_PARAGRAPH')}</p>
    </>;
};


export const TermsAndConditionsControl: React.FC<TermsAndConditionsValues & { onClick: ({ termsAndConditions, guideLines }: TermsAndConditionsValues) => void; }> = (
    { termsAndConditions, guideLines, onClick }) =>
{
    const { translate: t } = useL10n();

    const [_termsAndConditions, setTermsAndConditions] = useState(termsAndConditions);
    const [_guideLines, setGuideLines] = useState(guideLines);

    return <div className={styles.footer}>
        <Checkbox
            className={styles.checkbox}
            checked={termsAndConditions}
            onClick={(checked: boolean) =>
            {
                setTermsAndConditions(checked);
            }}
        >
            <IonLabel>
                {t('TERMS_AND_CONDITIONS_CHECKBOX_TERMS_AND_CONDTIONS')}
            </IonLabel>
        </Checkbox>

        <Checkbox
            className={styles.checkbox}
            checked={guideLines}
            onClick={(checked: boolean) =>
            {
                setGuideLines(checked);
            }}
        >
            <IonLabel>
                {t('TERMS_AND_CONDITIONS_CHECKBOX_SUPPORT_GUIDELINES')}
            </IonLabel>
        </Checkbox>

        <IonButton
            expand="block"
            disabled={!_termsAndConditions || !_guideLines}
            onClick={() => onClick && onClick({ termsAndConditions: _termsAndConditions, guideLines: _guideLines })}
        >
            {t("COM_FACPMANAGEMENT_REGRESSION_STATUS_OK")}
        </IonButton>
    </div>;

};

export const TermsAndConditionsCard: React.FC<{ children?: React.ReactNode; }> = ({ children }) =>
{
    return <div className={styles.modalCenter}>
        <IonCard className={styles.termsModal}>
            <IonCardContent
                className={styles.termsContent}
                style={{ whiteSpace: 'pre-line' }}
            >
                <TermsAndConditionsContent />
            </IonCardContent>
            {children}

        </IonCard>
    </div>;
};


export const TermsAndConditionPage: React.FC<{}> = () =>
{
    const { translate: t } = useL10n();
    const router = useIonRouter();

    return (
        <IonPage>
            <IonContent>
                <TermsAndConditionsCard >
                    <IonButton
                        expand="block"
                        onClick={() => router.goBack()}
                    >
                        {t("COM_FACPMANAGEMENT_REGRESSION_STATUS_OK")}
                    </IonButton>
                </TermsAndConditionsCard>
            </IonContent>
        </IonPage>
    )
        ;

};

export const TermsAndConditionsWrapper: React.FC<{ children: ReactNode; }> = ({ children }) =>
{
    const user = useMyProfile();
    const modifyUser = useModifyUser();

    const router = useIonRouter();
    const { generate } = useNamedRoutes();

    const handleOnClick = useCallback(async (data: TermsAndConditionsValues, user: User) =>
    {
        if (!data.termsAndConditions || !data.guideLines) return;

        const now = new Date();

        await modifyUser(user.id as string, {
            ...user,
            termsAcceptedAt: now,
            supportGuidelinesAcceptedAt: now,
        });

        const link = generate("home");
        router.push(link);

    }, [generate, modifyUser, router]);

    if (!user || !user.id) {
        return <IonSpinner />;
    }
    const needToShowModal = (!user.supportGuidelinesAcceptedAt || !user.termsAcceptedAt) && !isUserImpersonated();

    if (needToShowModal) {
        return (
            <TermsAndConditionsCard >
                <TermsAndConditionsControl
                    termsAndConditions={Boolean(user.termsAcceptedAt)}
                    guideLines={Boolean(user.supportGuidelinesAcceptedAt)}
                    onClick={(data) =>
                    {
                        handleOnClick(data, user);
                    }} />

            </TermsAndConditionsCard>
        );
    }
    return <>{children}</>;
};
